/**
 * MegaMenuToggle
 *
 * Keeps aria-expanded on the mega-menu trigger link in sync with the
 * existing CSS-driven show/hide behavior (:hover / :focus / :focus-within
 * on the parent <li>, see base.css `.header .list-menu--inline li:hover >
 * .header-mega-menu`). Does not change the visual show/hide mechanism —
 * only mirrors its state into ARIA for assistive tech.
 *
 * Markup contract (see snippets/header-mega-menu.liquid and
 * snippets/header--mega-menu.liquid): this element is inserted as an
 * inert sibling between the trigger <a> and the mega-menu panel <div>,
 * both of which remain direct children of the parent <li> so none of the
 * existing CSS combinators are affected.
 *
 *   <a aria-haspopup="true" aria-expanded="false" aria-controls="...">...</a>
 *   <mega-menu-toggle></mega-menu-toggle>
 *   <div class="header-mega-menu" id="...">...</div>
 */
class MegaMenuToggle extends HTMLElement {
	connectedCallback() {
		this.trigger = this.previousElementSibling;
		this.panel = this.nextElementSibling;
		this.li = this.closest("li");

		if (!this.trigger || !this.panel || !this.li) return;

		this.li.addEventListener("mouseenter", () => this.expand());
		this.li.addEventListener("mouseleave", () => this.collapse());
		this.li.addEventListener("focusin", () => this.expand());
		this.li.addEventListener("focusout", (event) => {
			if (!this.li.contains(event.relatedTarget)) {
				this.collapse();
			}
		});
		this.li.addEventListener("keydown", (event) => {
			if (event.key === "Escape") {
				this.collapse();
				this.trigger.focus();
			}
		});
	}

	expand() {
		this.trigger.setAttribute("aria-expanded", "true");
	}

	collapse() {
		this.trigger.setAttribute("aria-expanded", "false");
	}
}

if (!customElements.get("mega-menu-toggle")) {
	customElements.define("mega-menu-toggle", MegaMenuToggle);
}
